export type AuthRole = 'user' | 'admin';

const DEFAULT_USER_PATH = '/dashboard';
const DEFAULT_ADMIN_PATH = '/admin';
const MAX_RETURN_PATH_LENGTH = 2048;

const hasAllowedPrefix = (path: string, prefix: string): boolean => (
    path === prefix
    || path.startsWith(`${prefix}/`)
    || path.startsWith(`${prefix}?`)
    || path.startsWith(`${prefix}#`)
);

const decodeForSafetyCheck = (value: string): string | null => {
    let decoded = value;
    try {
        for (let pass = 0; pass < 2; pass += 1) {
            const next = decodeURIComponent(decoded);
            if (next === decoded) break;
            decoded = next;
        }
        return decoded;
    } catch {
        return null;
    }
};

/**
 * Accepts a same-origin path only. Protocol-relative URLs, backslashes,
 * credentials, control characters and encoded variants are rejected.
 */
export const sanitizeInternalReturnPath = (
    candidate: string | null | undefined,
    fallback: string = DEFAULT_USER_PATH,
): string => {
    if (typeof candidate !== 'string') return fallback;

    const value = candidate.trim();
    if (!value || value.length > MAX_RETURN_PATH_LENGTH) return fallback;
    if (!value.startsWith('/') || value.startsWith('//')) return fallback;
    if (/[\\\u0000-\u001f\u007f]/.test(value)) return fallback;

    const decoded = decodeForSafetyCheck(value);
    if (!decoded || !decoded.startsWith('/') || decoded.startsWith('//')) return fallback;
    if (/[\\\u0000-\u001f\u007f]/.test(decoded)) return fallback;

    try {
        const parsed = new URL(value, 'https://auth-return.invalid');
        if (parsed.origin !== 'https://auth-return.invalid') return fallback;
        if (parsed.username || parsed.password) return fallback;
        return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
        return fallback;
    }
};

/** Restricts post-auth handoffs to authenticated application areas. */
export const getRoleSafeReturnPath = (
    candidate: string | null | undefined,
    role: AuthRole,
): string => {
    const fallback = role === 'admin' ? DEFAULT_ADMIN_PATH : DEFAULT_USER_PATH;
    const path = sanitizeInternalReturnPath(candidate, fallback);

    if (hasAllowedPrefix(path, DEFAULT_USER_PATH)) return path;
    if (role === 'admin' && hasAllowedPrefix(path, DEFAULT_ADMIN_PATH)) return path;
    return fallback;
};

export const getDefaultAuthPath = (role: AuthRole): string => (
    role === 'admin' ? DEFAULT_ADMIN_PATH : DEFAULT_USER_PATH
);

export const getSafeSsoTargetOrigin = (targetDomain: string): string | null => {
    if (!targetDomain) return null;
    try {
        const target = new URL(targetDomain);
        const isLoopback = target.hostname === 'localhost'
            || target.hostname === '127.0.0.1'
            || target.hostname === '[::1]';
        if (target.protocol !== 'https:' && !(target.protocol === 'http:' && isLoopback)) return null;
        if (target.username || target.password) return null;
        return target.origin;
    } catch {
        return null;
    }
};

export const buildSsoHandoffUrl = (
    targetDomain: string,
    token: string,
    requestedReturnPath: string | null | undefined,
    role: AuthRole,
): string | null => {
    const origin = getSafeSsoTargetOrigin(targetDomain);
    if (!origin || !token) return null;

    const url = new URL('/sso', origin);
    url.searchParams.set('token', token);
    url.searchParams.set('return', getRoleSafeReturnPath(requestedReturnPath, role));
    return url.toString();
};
